/**
 * Componente: ModalTablaAccesos
 * 
 * Descripción:
 * Este componente representa un modal para agregar un nuevo acceso a un usuario.
 * Permite:
 * - Ingresar la contraseña del usuario para el acceso.
 * - Registrar el nombre de la máquina o entorno Windows asociado al acceso.
 * - Guardar los datos mediante un botón "Guardar", llamando al servicio `saveAccesosTabla`.
 * - Limpiar los campos cada vez que se abre el modal.
 * 
 * Tecnologías y librerías utilizadas:
 * - React (Hooks: useState, useEffect, useContext)
 * - PrimeReact: Dialog, Button, InputText
 * - Material UI: Label (aunque no se usa directamente en el código)
 * - Card: Componente personalizado para estructura y estilo de formulario
 * - Servicios internos: getLabel y baseLang para internacionalización, ConfigContext para configuración global
 * - Servicio API: saveAccesosTabla para guardar accesos de usuario
 * 
 * Props:
 * - visible: boolean que indica si el modal está visible o no.
 * - onHide: función que se ejecuta al cerrar el modal.
 * - userEdit: objeto del usuario al que se le está agregando el acceso.
 * 
 * Flujo general:
 * 1. Cuando `visible` cambia a true, se limpian los campos de password y Windows.
 * 2. El usuario ingresa la contraseña y el nombre de la máquina Windows.
 * 3. Al hacer click en "Guardar", se construye un objeto con los datos del acceso.
 * 4. Se llama al servicio `saveAccesosTabla` para guardar los datos en el backend.
 * 5. Si el guardado es exitoso, se cierra el modal mediante `onHide`.
 */

import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import Card from '../../../../../../Base/Components/Card/Card'
import { InputText } from 'primereact/inputtext';
import { Label } from '@mui/icons-material';
import { getLabel } from '../../../../../../Base/Services/Language/LanguageService';
import { baseLang } from '../../../../../../Base/Lang/BaseLang';
import { ConfigContext } from '../../../../../../Base/Context/ConfigContext/ConfigContext';
import { useContext, useState, useEffect } from 'react'
import { saveAccesosTabla } from '../../../../../../API/IT/Usuarios/AccesosUserRequets'

export const ModalTablaAccesos = ({ visible, onHide, userEdit }) => {

    /** Contexto global */
    const { config } = useContext(ConfigContext);

    /** Estados locales */
    const [password, setPassword] = useState('');
    const [windows, setWindows] = useState('');
    const [dataUserIPS, setDataUserIPS] = useState('');
    /**
     * Maneja el guardado de un nuevo acceso para el usuario
     * @async
     * @function handleSave
     * @returns {Promise<void>} Envia los datos recibidos del frontend al banckend. Una vez se guarda el nuevo acceso, se cierra el modal.
     */
    const handleSave = async () => {

        if (!dataUserIPS) {
            console.error("Usuario es obligatorio");
            return;
        }

        const accesoData = {
            UserIPS: dataUserIPS, 
            Password: password,
            Windows: windows
        };

        try {
            await saveAccesosTabla(accesoData);
            if (typeof onHide === 'function') onHide();
        } catch (error) {
            console.error('Error guardando acceso:', error);
        }
    };

    // const handleSave = async () => {
    //     if (!userEdit) return; // Si no hay usuario seleccionado, no hace nada

    //     const accesoData = {
    //         UserIPS: userEdit.Usuario, // Usuario al que se le asigna el acceso
    //         Password: password, // Password ingresada
    //         Windows: windows // Nombre de acceso asignado
    //     };

    //     try {
    //         const res = await saveAccesosTabla(accesoData); // Llama al API para guardar
    //         if (typeof onHide === 'function') onHide(); // Cierra el modal
    //     } catch (error) {
    //         console.error('Error guardando acceso:', error);
    //     }
    // };

    /**
     * Limpia los campos de password y windows cada vez que se abre el modal
     * @function limpiarCampos
     * @param {boolean} visible - Indica si el modal esta visible
     */
    useEffect(() => {
        if (visible) {
            setPassword('');
            setWindows('');

            // 🔹 Si viene userEdit, autocompleta Usuario
            if (userEdit?.Usuario) {
                setDataUserIPS(userEdit.Usuario);
            } else {
                setDataUserIPS('');
            }
        }
    }, [visible, userEdit]);

    // useEffect(() => {
    //     if (visible) {
    //         setPassword('');
    //         setWindows('');
    //     }
    // }, [visible]);

    return (
        <Dialog
            header={`Nuevo Acceso - ${userEdit?.Usuario || ''}`}
            visible={visible}
            modal
            style={{ width: '500px' }}
            onHide={onHide}
        >
            {/* Card para contenido estructurado */}
            <Card style={{ display: 'flex', positions: 'fixed', gap: '10px' }}
                titulo={'Añadir nuevo acceso'}
                contenido={
                    <div className='d-flex'>
                        <div className='d-flex' style={{ flexDirection: 'column', width: '100%' }}>
                            <div className='d-flex' style={{ flexGrow: 5 }}>
                                {/* FILA 1 */}
                                <div className='p-2 d-flex gap' style={{ flexDirection: 'column' }} >
                                    {/* Input de Usuario */}
                                    <div className='field'>
                                        <span className='p-float-label'>
                                            <InputText
                                                id='Usuario'
                                                name="Usuario"
                                                value={dataUserIPS}
                                                onChange={e => setDataUserIPS(e.target.value)}
                                                disabled={!!userEdit?.Usuario}
                                                style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                                            />
                                            <label htmlFor="Usuario">Usuario</label>
                                        </span>
                                    </div>

                                    <div className='p-2 d-flex flex-column gap-3'>
                                        {/* Input de Contraseña */}
                                        <div className='field'>
                                            <span className='p-float-label'>
                                                <InputText
                                                    id='Contraseña'
                                                    name="Contraseña"
                                                    value={password}
                                                    onChange={e => setPassword(e.target.value)}
                                                    style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                                                />
                                                <label htmlFor="username">Contraseña</label>
                                            </span>
                                        </div>
                                        {/* Input de Windows */}
                                        <div className='field'>
                                            <span className='p-float-label'>
                                                <InputText
                                                    id='Windows'
                                                    name="Windows"
                                                    value={windows}
                                                    onChange={e => setWindows(e.target.value)}
                                                    style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                                                />
                                                <label htmlFor="Windows">Windows</label>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            />

            {/* Botón de Guardar */}
            <Button
                label="Guardar"
                icon="pi pi-save"
                type="button"
                onClick={handleSave}
            />

        </Dialog>
    );
};

export default ModalTablaAccesos;


